import { useEffect, useRef } from "react";
import Player from "video.js/dist/types/player";
import { PostVideoView, sendVideoViewBeacon } from "../../../Api/Reporting";

const MIN_WATCH_SECONDS = 5;
const TICK_INTERVAL_MS = 1000;        // accumulate watched time + fire the first beacon
const HEARTBEAT_INTERVAL_MS = 25000;  // cumulative heartbeat while playing

interface VideoViewAuth {
    // Refreshes an expired token before returning — used on the live path so a
    // stale token doesn't drop the view into auth_failed. Returns null when
    // logged out (the view is then reported anonymously).
    getValidAccessToken: () => Promise<string | null>;
    // Synchronous token read for the unload/hidden flush, where we can't await a
    // refresh. null → reported anonymously.
    getAccessTokenSync: () => string | null;
    // True when a session exists (valid access token OR a live refresh token),
    // even if the in-memory access token is momentarily expired. Lets us avoid
    // emitting an anonymous beacon for a logged-in user during a transient refresh
    // failure or the post-reload bootstrap window — the server would otherwise
    // re-flag that anonymous view of paid content as auth_failed.
    doesAuthTokenExist: () => boolean;
}

/**
 * Reports video views per the client beacon spec: a first beacon once playback
 * crosses ~5s, a CUMULATIVE heartbeat every ~25s while playing, and a flush on
 * pause / ended / tab-hidden / unload. `watch_seconds` is the cumulative
 * wall-clock time the video was actually playing (pauses, seeking and tab-hidden
 * time excluded), so the server's max-per-session dedup yields an accurate watch
 * time instead of a single ~5s row. Logged-out viewers are reported too (no
 * Authorization header).
 */
const useVideoView = (
    player: Player | null,
    videoId: number | undefined,
    auth: VideoViewAuth
) => {
    const watchedRef = useRef(0);              // cumulative seconds actually played
    const lastTickRef = useRef<number | null>(null);
    const lastSentRef = useRef(0);             // highest watch_seconds already queued
    const controllerRef = useRef(new AbortController());
    // Last bearer resolved on the live path. The unload/hidden flush can't await a
    // refresh, so it falls back to this (still typically valid for ~14min, and
    // kept fresh by the ~25s heartbeat) instead of sending the final beacon
    // token-less for a logged-in viewer.
    const warmTokenRef = useRef<string | null>(null);

    // Keep the latest auth accessors without re-subscribing player listeners.
    const authRef = useRef(auth);
    authRef.current = auth;

    useEffect(() => {
        if (!player || !videoId) return;

        // Add the wall-clock time since the last tick, but only while genuinely
        // playing. Pauses, seeking and hidden tabs don't count.
        const accumulate = () => {
            // On navigation the player is disposed before this hook's cleanup
            // runs, after which player.paused() throws (its tech is null). Keep
            // the last accumulated total and bail.
            if (player.isDisposed()) {
                lastTickRef.current = null;
                return;
            }
            const playing =
                !player.paused() && !player.seeking() && !player.ended() && !document.hidden;
            const now = performance.now();
            if (playing && lastTickRef.current != null) {
                watchedRef.current += (now - lastTickRef.current) / 1000;
            }
            lastTickRef.current = playing ? now : null;
        };

        // Live send (page alive): refresh an expired token first so signed-in
        // views are attributed correctly. Skip when there's nothing new to report.
        const send = async () => {
            accumulate();
            const watched = Math.floor(watchedRef.current);
            if (watched < MIN_WATCH_SECONDS || watched <= lastSentRef.current) return;
            const token = await authRef.current.getValidAccessToken();
            // Known logged-in but no resolvable token right now (transient refresh
            // failure, or bootstrap still in flight after a reload): don't post
            // anonymously and don't advance lastSentRef, so a later tick/heartbeat
            // retries once the token resolves. A genuinely logged-out viewer
            // (doesAuthTokenExist === false) falls through and is reported anonymously.
            if (token === null && authRef.current.doesAuthTokenExist()) return;
            if (token) warmTokenRef.current = token;
            lastSentRef.current = watched;
            void PostVideoView(videoId, watched, token, controllerRef.current);
        };

        // Unload / hidden flush: synchronous, keepalive transport, no token
        // refresh. `force` guarantees the final value lands even if a heartbeat
        // already sent it (repeated posts are cheap — the server dedups them).
        const flush = (force = false) => {
            accumulate();
            const watched = Math.floor(watchedRef.current);
            if (watched < MIN_WATCH_SECONDS) return;
            if (!force && watched <= lastSentRef.current) return;
            lastSentRef.current = watched;
            // Prefer a still-valid in-memory AT; fall back to the last good bearer
            // from the live path (can't refresh during unload). A logged-out viewer
            // yields null on both and is reported anonymously.
            const token = authRef.current.getAccessTokenSync() ?? warmTokenRef.current;
            sendVideoViewBeacon(videoId, watched, token);
        };

        // 1s tick: keep the watched total current and fire the first beacon as
        // soon as we cross the 5s threshold (the heartbeat alone wouldn't fire
        // until ~25s).
        const onTick = () => {
            accumulate();
            if (lastSentRef.current === 0 && Math.floor(watchedRef.current) >= MIN_WATCH_SECONDS) {
                void send();
            }
        };

        const onPause = () => { void send(); };
        const onEnded = () => { void send(); };
        const onVisibility = () => { if (document.hidden) flush(); };
        const onPageHide = () => { flush(); };

        const tickId = window.setInterval(onTick, TICK_INTERVAL_MS);
        const heartbeatId = window.setInterval(() => { void send(); }, HEARTBEAT_INTERVAL_MS);

        player.on("pause", onPause);
        player.on("ended", onEnded);
        document.addEventListener("visibilitychange", onVisibility);
        window.addEventListener("pagehide", onPageHide);

        const controller = controllerRef.current;
        return () => {
            window.clearInterval(tickId);
            window.clearInterval(heartbeatId);
            player.off("pause", onPause);
            player.off("ended", onEnded);
            document.removeEventListener("visibilitychange", onVisibility);
            window.removeEventListener("pagehide", onPageHide);
            flush(true);                   // final flush on unmount / navigation
            controller.abort();            // cancel any in-flight live heartbeat
        };
    }, [player, videoId]);
};

export default useVideoView;
